import { selectDeployments } from "@app/deployment";
import { createSelector } from "@app/fx";
import { type WebState, schema } from "@app/schema";
import { selectSourcesAsList } from "@app/source";
import type { DeployServiceRow, DeploySource, Deployment } from "@app/types";
import {
  type DeployAppRow,
  findAppById,
  selectApps,
  selectAppsByOrgAsList,
} from "../app";
import { selectAppsViaAppExternalAwsRdsConnectionsByExternalAwsResourceId } from "../app-external-aws-rds-connection";
import { computeCostId } from "../cost";
import { selectDashboardsAsList } from "../dashboard";
import {
  type DeployDatabaseRow,
  selectDatabasesByOrgAsList,
} from "../database";
import {
  findDatabaseImageById,
  selectDatabaseImages,
} from "../database-images";
import { findDiskById, selectDisks } from "../disk";
import { selectDiskStorageGbByServiceId } from "../disk-attachment";
import { selectAppsByCertId } from "../endpoint";
import {
  findEnvById,
  hasDeployEnvironment,
  selectEnvironments,
  selectEnvironmentsByOrg,
} from "../environment";
import { type DeployLlmKeyRow, selectLlmKeysByOrgAsList } from "../llm-key";
import {
  type ScaleRecommendationState,
  deriveScaleRecommendationState,
} from "../manual_scale_recommendation";
import {
  findOperationsByAppId,
  findOperationsByDbId,
  selectOperationsAsList,
} from "../operation";
import {
  calcMetrics,
  findServiceById,
  findServicesByAppId,
  selectServices,
  selectServicesByOrgId,
  serviceCommandText,
} from "../service";

export const selectServicesForTable = createSelector(
  selectEnvironmentsByOrg,
  selectApps,
  selectServicesByOrgId,
  schema.manualScaleRecommendations.selectTableAsList,
  schema.costs.selectTable,
  schema.serviceSizingPolicies.selectTable,
  selectDiskStorageGbByServiceId,
  (envs, apps, services, recommendations, costs, policies, diskStorageGb) =>
    services
      // making sure we have a valid environment associated with it
      .filter((service) => {
        const env = findEnvById(envs, { id: service.environmentId });
        return hasDeployEnvironment(env);
      })
      // exclude database services since customers only know of them as App Services.
      .filter((service) => service.appId)
      .map((service): DeployServiceRow => {
        const env = findEnvById(envs, { id: service.environmentId });
        let resourceHandle = "";
        if (service.appId) {
          const app = findAppById(apps, { id: service.appId });
          resourceHandle = app.handle;
        } else {
          resourceHandle = "Unknown";
        }
        const recommendation = recommendations.find(
          (r) => r.serviceId === service.id,
        );
        const costItem = schema.costs.findById(costs, {
          id: computeCostId("Service", service.id),
        });
        const cost = costItem.estCost;
        const policy =
          policies[service.serviceSizingPolicyId] ??
          schema.serviceSizingPolicies.empty;
        let savings = recommendation?.costSavings || 0;
        if (policy.scalingEnabled) {
          // savings is 0 for both autoscaling and right-sized which
          // means they would be interleaved during a sort so to fix that we
          // set autoscaling to something that can be clumped together
          savings = -0.1;
        }
        const scaleRec = deriveScaleRecommendationState(
          {
            id: service.id,
            instanceClass: service.instanceClass,
            containerMemoryLimitMb: service.containerMemoryLimitMb,
            serviceSizingPolicyId: service.serviceSizingPolicyId,
          },
          policy,
          recommendation,
        );

        return {
          ...service,
          envHandle: env.handle,
          resourceHandle,
          savings,
          cost,
          scaleRec,
          command: serviceCommandText(service),
          diskStorageSort: diskStorageGb[service.id] ?? 0,
        };
      })
      .sort((a, b) => b.id.localeCompare(a.id, undefined, { numeric: true })),
);

export const selectServiceRowsByAppId = createSelector(
  selectServicesForTable,
  (_: WebState, p: { appId: string }) => p.appId,
  (services, appId) => {
    return services.filter((service) => service.appId === appId);
  },
);

export const selectAppsForTable = createSelector(
  selectAppsByOrgAsList,
  selectEnvironments,
  selectOperationsAsList,
  selectServices,
  selectDeployments,
  schema.costs.selectTable,
  (apps, envs, ops, services, deployments, costs) =>
    apps
      .map((app): DeployAppRow => {
        const env = findEnvById(envs, { id: app.environmentId });
        const appOps = findOperationsByAppId(ops, app.id);
        const lastOperation = appOps?.[0] || schema.operations.empty;
        const currentDeployment = schema.deployments.findById(deployments, {
          id: app.currentDeploymentId,
        });
        const appServices = findServicesByAppId(
          Object.values(services),
          app.id,
        );
        const metrics = calcMetrics(appServices);
        const costItem = schema.costs.findById(costs, {
          id: computeCostId("App", app.id),
        });
        const cost = costItem.estCost;

        return {
          ...app,
          ...metrics,
          envHandle: env.handle,
          lastOperation,
          currentDeployment,
          gitRef: currentDeployment.gitRef,
          gitCommitSha: currentDeployment.gitCommitSha,
          dockerImageName: currentDeployment.dockerImage,
          lastDeployed:
            currentDeployment.id !== "" ? currentDeployment.createdAt : "",
          cost,
          totalServices: appServices.length,
          services: appServices,
        };
      })
      .sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true })),
);

const computeSearchMatch = (app: DeployAppRow, search: string): boolean => {
  const gitRef = app.gitRef.toLocaleLowerCase();
  const gitCommitSha = app.gitCommitSha.toLocaleLowerCase();
  const dockerImageName = app.dockerImageName.toLocaleLowerCase();
  const handle = app.handle.toLocaleLowerCase();
  const envHandle = app.envHandle.toLocaleLowerCase();

  let lastOpUser = "";
  let lastOpType = "";
  let lastOpStatus = "";
  if (app.lastOperation) {
    lastOpUser = app.lastOperation.userName.toLocaleLowerCase();
    lastOpType = app.lastOperation.type.toLocaleLowerCase();
    lastOpStatus = app.lastOperation.status.toLocaleLowerCase();
  }

  const gitRefMatch = gitRef.includes(search);
  const gitCommitShaMatch = gitCommitSha.includes(search);
  const dockerImageNameMatch = dockerImageName.includes(search);
  const handleMatch = handle.includes(search);
  const envMatch = envHandle.includes(search);
  const userMatch = lastOpUser !== "" && lastOpUser.includes(search);
  const opMatch = lastOpType !== "" && lastOpType.includes(search);
  const opStatusMatch = lastOpStatus !== "" && lastOpStatus.includes(search);
  const idMatch = search === app.id;

  return (
    gitRefMatch ||
    gitCommitShaMatch ||
    dockerImageNameMatch ||
    handleMatch ||
    envMatch ||
    opMatch ||
    opStatusMatch ||
    userMatch ||
    idMatch
  );
};

const createAppSortFn = (
  sortBy: keyof DeployAppRow,
  sortDir: "asc" | "desc",
) => {
  return (a: DeployAppRow, b: DeployAppRow) => {
    if (sortBy === "cost") {
      if (sortDir === "asc") {
        return a.cost - b.cost;
      }
      return b.cost - a.cost;
    }

    if (sortBy === "handle") {
      if (sortDir === "asc") {
        return a.handle.localeCompare(b.handle);
      }
      return b.handle.localeCompare(a.handle);
    }

    if (sortBy === "gitRef") {
      const aRefOrSha = [a.gitRef, a.gitCommitSha].join("@");
      const bRefOrSha = [b.gitRef, b.gitCommitSha].join("@");
      if (sortDir === "asc") {
        return aRefOrSha.localeCompare(bRefOrSha);
      }
      return bRefOrSha.localeCompare(aRefOrSha);
    }

    if (sortBy === "dockerImageName") {
      if (sortDir === "asc") {
        return a.dockerImageName.localeCompare(b.dockerImageName);
      }
      return b.dockerImageName.localeCompare(a.dockerImageName);
    }

    if (sortBy === "id") {
      if (sortDir === "asc") {
        return a.id.localeCompare(b.id, undefined, { numeric: true });
      }
      return b.id.localeCompare(a.id, undefined, { numeric: true });
    }

    if (sortBy === "totalServices") {
      if (sortDir === "asc") {
        return a.totalServices - b.totalServices;
      }
      return b.totalServices - a.totalServices;
    }

    if (sortBy === "envHandle") {
      if (sortDir === "asc") {
        return a.envHandle.localeCompare(b.envHandle);
      }
      return b.envHandle.localeCompare(a.envHandle);
    }

    if (sortBy === "lastDeployed") {
      const aEmpty = !a.lastDeployed;
      const bEmpty = !b.lastDeployed;
      if (aEmpty && bEmpty) {
        return 0;
      }
      if (sortDir === "desc") {
        // Newest first; never-deployed at bottom
        if (aEmpty) {
          return 1;
        }
        if (bEmpty) {
          return -1;
        }
      } else {
        // Oldest first; never-deployed at top
        if (aEmpty) {
          return -1;
        }
        if (bEmpty) {
          return 1;
        }
      }
      const aTime = new Date(a.lastDeployed).getTime();
      const bTime = new Date(b.lastDeployed).getTime();
      if (sortDir === "asc") {
        return aTime - bTime;
      }
      return bTime - aTime;
    }

    if (sortDir === "asc") {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  };
};

export const selectAppsForTableByCertId = createSelector(
  selectAppsForTable,
  selectAppsByCertId,
  (tableApps, certApps): DeployAppRow[] => {
    const certAppIds = new Set(certApps.map((a) => a.id));
    return tableApps.filter((a) => certAppIds.has(a.id));
  },
);

export const selectAppsForTableByExternalAwsResourceId = createSelector(
  selectAppsForTable,
  selectAppsViaAppExternalAwsRdsConnectionsByExternalAwsResourceId,
  (tableApps, resourceApps): DeployAppRow[] => {
    const resourceAppIds = new Set(resourceApps.map((a) => a.id));
    return tableApps.filter((a) => resourceAppIds.has(a.id));
  },
);

export const selectAppsForTableByEnvId = createSelector(
  selectAppsForTable,
  (_: WebState, props: { envId: string }) => props.envId,
  (apps, envId): DeployAppRow[] => {
    if (envId === "") return apps;
    return apps.filter((app) => app.environmentId === envId);
  },
);

export const selectAppsForTableBySourceId = createSelector(
  selectAppsForTable,
  (_: WebState, props: { sourceId: string }) => props.sourceId,
  (apps, sourceId): DeployAppRow[] => {
    if (sourceId === "") return apps;
    return apps.filter((app) => app.currentSourceId === sourceId);
  },
);

export const selectAppsForTableSearchByEnvironmentId = createSelector(
  selectAppsForTable,
  (_: WebState, props: { search: string }) => props.search.toLocaleLowerCase(),
  (_: WebState, props: { envId?: string }) => props.envId || "",
  (_: WebState, p: { sortBy: keyof DeployAppRow }) => p.sortBy,
  (_: WebState, p: { sortDir: "asc" | "desc" }) => p.sortDir,
  (apps, search, envId, sortBy, sortDir): DeployAppRow[] => {
    const sortFn = createAppSortFn(sortBy, sortDir);

    if (search === "" && envId === "") {
      return [...apps].sort(sortFn);
    }

    const results = apps.filter((app) => {
      const searchMatch = computeSearchMatch(app, search);
      const envIdMatch = envId !== "" && app.environmentId === envId;

      if (envId !== "") {
        if (search !== "") {
          return envIdMatch && searchMatch;
        }

        return envIdMatch;
      }

      return searchMatch;
    });

    return results.sort(sortFn);
  },
);

export const selectAppsForTableSearch = createSelector(
  selectAppsForTable,
  (_: WebState, props: { search: string }) => props.search.toLocaleLowerCase(),
  (_: WebState, p: { sortBy: keyof DeployAppRow }) => p.sortBy,
  (_: WebState, p: { sortDir: "asc" | "desc" }) => p.sortDir,
  (apps, search, sortBy, sortDir): DeployAppRow[] => {
    const sortFn = createAppSortFn(sortBy, sortDir);

    if (search === "") {
      return [...apps].sort(sortFn);
    }

    return apps.filter((app) => computeSearchMatch(app, search)).sort(sortFn);
  },
);

export const selectAppsForTableSearchBySourceId = createSelector(
  selectAppsForTable,
  (_: WebState, props: { search: string }) => props.search.toLocaleLowerCase(),
  (_: WebState, props: { sourceId: string }) => props.sourceId,
  (_: WebState, p: { sortBy: keyof DeployAppRow }) => p.sortBy,
  (_: WebState, p: { sortDir: "asc" | "desc" }) => p.sortDir,
  (apps, search, sourceId, sortBy, sortDir): DeployAppRow[] => {
    const sortFn = createAppSortFn(sortBy, sortDir);

    if (search === "" && sourceId === "") {
      return [...apps].sort(sortFn);
    }

    const results = apps.filter((app) => {
      const searchMatch = computeSearchMatch(app, search);
      const sourceIdMatch = sourceId !== "" && app.currentSourceId === sourceId;

      if (sourceId !== "") {
        if (search !== "") {
          return sourceIdMatch && searchMatch;
        }

        return sourceIdMatch;
      }

      return searchMatch;
    });

    return results.sort(sortFn);
  },
);

export interface GitCommit {
  sha: string;
  ref: string;
  message: string;
  date: string | null;
  url: string;
}

export interface DeploySourceRow extends DeploySource {
  apps: DeployAppRow[];
  deployments: Deployment[];
  liveCommits: GitCommit[];
}

export const selectSourcesForTable = createSelector(
  selectSourcesAsList,
  selectAppsForTable,
  (sources, apps) =>
    sources.map<DeploySourceRow>((source) => {
      const sourceApps = apps.filter(
        (app) => app.currentSourceId === source.id,
      );
      const sourceDeployments = sourceApps.map((app) => app.currentDeployment);
      const distinctCommits = sourceDeployments.reduce<
        Record<string, GitCommit>
      >((commits, deployment) => {
        const sha = deployment.gitCommitSha;

        if (!sha || Object.hasOwn(commits, sha)) {
          return commits;
        }

        commits[sha] = {
          sha,
          ref: deployment.gitRef,
          message: deployment.gitCommitMessage,
          date: deployment.gitCommitTimestamp,
          url: deployment.gitCommitUrl,
        };

        return commits;
      }, {});
      const liveCommits = Object.values(distinctCommits).sort((a, b) => {
        if (!a.date || !b.date) {
          return 0;
        }

        return new Date(b.date).getTime() - new Date(a.date).getTime();
      });

      return {
        ...source,
        apps: sourceApps,
        deployments: sourceDeployments,
        liveCommits,
      };
    }),
);

export const selectSourcesForTableSearch = createSelector(
  selectSourcesForTable,
  (_: WebState, props: { search: string }) => props.search.toLocaleLowerCase(),
  (_: WebState, p: { sortBy: keyof DeploySourceRow }) => p.sortBy,
  (_: WebState, p: { sortDir: "asc" | "desc" }) => p.sortDir,
  (sources, search, sortBy, sortDir) => {
    const sortFn = (a: DeploySourceRow, b: DeploySourceRow) => {
      const left = (sortDir === "asc" ? a : b)[sortBy];
      const right = (sortDir === "asc" ? b : a)[sortBy];

      if (sortBy === "liveCommits") {
        return left.length - right.length;
      }

      if (sortBy === "apps") {
        return left.length - right.length;
      }

      if (sortBy === "displayName") {
        return (<string>left).localeCompare(<string>right);
      }

      return 0;
    };

    if (search === "") {
      return [...sources].sort(sortFn);
    }

    const results = sources.filter((source) => {
      return source.displayName.includes(search);
    });

    return results.sort(sortFn);
  },
);

const selectSearchProp = (_: WebState, props: { search: string }) =>
  props.search.toLocaleLowerCase();

const computeSearchMatchDb = (
  db: DeployDatabaseRow,
  search: string,
): boolean => {
  const handle = (db.handle || "").toLocaleLowerCase();
  const envHandle = (db.envHandle || "").toLocaleLowerCase();
  const dbType = (db.type || "").toLocaleLowerCase();
  const dbImage = (db.imageDesc || "").toLocaleLowerCase();

  let lastOpUser = "";
  let lastOpType = "";
  let lastOpStatus = "";
  if (db.lastOperation) {
    lastOpUser = db.lastOperation.userName.toLocaleLowerCase();
    lastOpType = db.lastOperation.type.toLocaleLowerCase();
    lastOpStatus = db.lastOperation.status.toLocaleLowerCase();
  }

  const handleMatch = handle.includes(search);
  const envMatch = envHandle.includes(search);
  const userMatch = lastOpUser !== "" && lastOpUser.includes(search);
  const opMatch = lastOpType !== "" && lastOpType.includes(search);
  const opStatusMatch = lastOpStatus !== "" && lastOpStatus.includes(search);
  const dbTypeMatch = dbType.includes(search);
  const imgDescMatch = dbImage.includes(search);
  const idMatch = search === db.id;

  return (
    handleMatch ||
    imgDescMatch ||
    dbTypeMatch ||
    envMatch ||
    opMatch ||
    opStatusMatch ||
    userMatch ||
    idMatch
  );
};

const createDatabaseSortFn = (
  sortBy: keyof DeployDatabaseRow,
  sortDir: "asc" | "desc",
) => {
  return (a: DeployDatabaseRow, b: DeployDatabaseRow) => {
    if (sortBy === "cost") {
      if (sortDir === "asc") {
        return a.cost - b.cost;
      }
      return b.cost - a.cost;
    }

    if (sortBy === "savings") {
      if (sortDir === "asc") {
        return a.savings - b.savings;
      }
      return b.savings - a.savings;
    }

    if (sortBy === "handle") {
      if (sortDir === "asc") {
        return a.handle.localeCompare(b.handle);
      }
      return b.handle.localeCompare(a.handle);
    }

    if (sortBy === "id") {
      if (sortDir === "asc") {
        return a.id.localeCompare(b.id, undefined, { numeric: true });
      }
      return b.id.localeCompare(a.id, undefined, { numeric: true });
    }

    if (sortBy === "diskSize") {
      if (sortDir === "asc") {
        return a.diskSize - b.diskSize;
      }
      return b.diskSize - a.diskSize;
    }

    if (sortBy === "containerSize") {
      if (sortDir === "asc") {
        return a.containerSize - b.containerSize;
      }
      return b.containerSize - a.containerSize;
    }

    if (sortBy === "envHandle") {
      if (sortDir === "asc") {
        return a.envHandle.localeCompare(b.envHandle);
      }
      return b.envHandle.localeCompare(a.envHandle);
    }

    if (sortBy === "enableBackups") {
      if (sortDir === "asc") {
        return Number(a.enableBackups) - Number(b.enableBackups);
      }
      return Number(b.enableBackups) - Number(a.enableBackups);
    }

    if (sortDir === "asc") {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  };
};

export const selectDatabasesForTable = createSelector(
  selectDatabasesByOrgAsList,
  selectEnvironments,
  selectOperationsAsList,
  selectDisks,
  selectServices,
  selectDatabaseImages,
  schema.serviceSizingPolicies.selectTableAsList,
  schema.manualScaleRecommendations.selectTableAsList,
  schema.costs.selectTable,
  (dbs, envs, ops, disks, services, images, policies, recommendations, costs) =>
    dbs
      .map((dbb): DeployDatabaseRow => {
        const env = findEnvById(envs, { id: dbb.environmentId });
        const dbOps = findOperationsByDbId(ops, dbb.id);
        let lastOperation = schema.operations.empty;
        if (dbOps.length > 0) {
          lastOperation = dbOps[0];
        }
        const disk = findDiskById(disks, { id: dbb.diskId });
        const service = findServiceById(services, { id: dbb.serviceId });
        const costItem = schema.costs.findById(costs, {
          id: computeCostId("Database", dbb.id),
        });
        const cost = costItem.estCost;
        const policy = policies.find(
          (p) => p.id === service.serviceSizingPolicyId,
        );
        const metrics = calcMetrics([service]);
        const img = findDatabaseImageById(images, { id: dbb.databaseImageId });
        const recommendation = recommendations.find(
          (s) => s.serviceId === dbb.serviceId,
        );
        const scaleRec = deriveScaleRecommendationState(
          {
            id: dbb.serviceId,
            instanceClass: service.instanceClass,
            containerMemoryLimitMb: service.containerMemoryLimitMb,
            serviceSizingPolicyId: service.serviceSizingPolicyId,
          },
          policy,
          recommendation,
        );
        return {
          ...dbb,
          imageDesc: img.description,
          envHandle: env.handle,
          lastOperation,
          diskSize: disk.size,
          cost,
          containerSize: metrics.totalMemoryLimit / 1024,
          containerCpu: metrics.totalCPU,
          savings: recommendation?.costSavings || 0,
          scaleRec,
        };
      })
      .sort((a, b) => b.id.localeCompare(a.id, undefined, { numeric: true })),
);

export const selectDatabasesForTableSearch = createSelector(
  selectDatabasesForTable,
  selectSearchProp,
  (_: WebState, p: { sortBy: keyof DeployDatabaseRow }) => p.sortBy,
  (_: WebState, p: { sortDir: "asc" | "desc" }) => p.sortDir,
  schema.manualScaleRecommendations.selectTableAsList,
  schema.serviceSizingPolicies.selectTable,
  selectServices,
  (
    dbs,
    search,
    sortBy,
    sortDir,
    recs,
    policies,
    services,
  ): DeployDatabaseRow[] => {
    const sortFn = createDatabaseSortFn(sortBy, sortDir);
    const { tags, text } = parseSearchTags(search);
    const tagsEmpty = Object.keys(tags).length === 0;

    // No tags and no text → just sort base list
    if (tagsEmpty && text === "") {
      return [...dbs].sort(sortFn);
    }

    // Text-only path (avoid recs/policies work)
    if (tagsEmpty) {
      const textFiltered =
        text === "" ? dbs : dbs.filter((db) => computeSearchMatchDb(db, text));
      return [...textFiltered].sort(sortFn);
    }

    // Tags present: apply tag filters, then optional text filter
    const scaleRecommendationStateForDb = (
      db: DeployDatabaseRow,
    ): ScaleRecommendationState => {
      const service = services[db.serviceId];
      const recommendation = recs.find((r) => r.serviceId === db.serviceId);
      const policyId = service?.serviceSizingPolicyId ?? "";
      const policy = policies[policyId];
      const serviceForRecEval = service
        ? {
            id: service.id,
            instanceClass: service.instanceClass,
            containerMemoryLimitMb: service.containerMemoryLimitMb,
            serviceSizingPolicyId: service.serviceSizingPolicyId,
          }
        : undefined;
      return deriveScaleRecommendationState(
        serviceForRecEval,
        policy,
        recommendation,
      );
    };

    let filtered = dbs.filter((db) => {
      if (tags.scalerec === "all") {
        const state = scaleRecommendationStateForDb(db);
        if (state !== "scale_up" && state !== "scale_down") return false;
      } else if (tags.scaledownrec === "true" || tags.scaleuprec === "true") {
        const state = scaleRecommendationStateForDb(db);
        if (tags.scaledownrec === "true" && state !== "scale_down") {
          return false;
        }
        if (tags.scaleuprec === "true" && state !== "scale_up") {
          return false;
        }
      }

      return true;
    });

    if (text !== "") {
      filtered = filtered.filter((db) => computeSearchMatchDb(db, text));
    }

    return [...filtered].sort(sortFn);
  },
);

export const selectDatabasesForTableSearchByEnvironmentId = createSelector(
  selectDatabasesForTable,
  selectSearchProp,
  (_: WebState, props: { envId?: string }) => props.envId || "",
  (dbs, search, envId): DeployDatabaseRow[] => {
    if (search === "" && envId === "") {
      return dbs;
    }

    return dbs.filter((db) => {
      const searchMatch = computeSearchMatchDb(db, search);
      const envIdMatch = envId !== "" && db.environmentId === envId;

      if (envId !== "") {
        if (search !== "") {
          return envIdMatch && searchMatch;
        }

        return envIdMatch;
      }

      return searchMatch;
    });
  },
);

export const selectDatabasesByEnvIdForTable = createSelector(
  selectDatabasesForTable,
  (_: WebState, props: { envId: string }) => props.envId,
  (rows, envId) => rows.filter((r) => r.environmentId === envId),
);

export const selectDashboardsForTableSearch = createSelector(
  selectDashboardsAsList,
  (_: WebState, props: { search: string }) => props.search,
  (dashboards, search) => {
    return dashboards.filter((dashboard) =>
      dashboard.name.toLowerCase().includes(search.toLowerCase()),
    );
  },
);

/**
 * Parse key:value tags from a search string.
 * - Non `key:value` tokens are returned as free-text in `text`.
 * - Supported tags: autoscaling, scaledownrec, scaleuprec, scalerec. scalerec value "all" = both scale-up and scale-down recs.
 * - Extra spaces are ignored and unused tokens are returned as free-text. Spaces are URL-encoded as %20 by the browser/router, e.g.:
 * @param rawSearch Raw search input (whitespace-delimited; extra spaces ignored).
 * @returns Object with `tags` (map of tag → value) and `text` (remaining free-text).
 * @example
 * parseSearchTags("myapp autoscaling:true")
 * // => { tags: { autoscaling: "true" }, text: "myapp" }
 */
export const parseSearchTags = (
  rawSearch: string,
): {
  tags: Record<string, string>;
  text: string;
} => {
  const tags: Record<string, string> = {};
  if (!rawSearch) return { tags, text: "" };
  const tokens = rawSearch.toLowerCase().trim().split(/\s+/);
  const textTokens: string[] = [];
  for (const tok of tokens) {
    const m = tok.match(/^(\w+):(.*)$/);
    if (m) {
      const key = m[1];
      const val = m[2];
      if (
        key === "autoscaling" ||
        key === "scaledownrec" ||
        key === "scaleuprec" ||
        key === "scalerec"
      ) {
        tags[key] = val;
        continue;
      }
    }
    textTokens.push(tok);
  }
  return { tags, text: textTokens.join(" ") };
};

// LLM Key selectors
export const selectLlmKeysForTable = createSelector(
  selectEnvironmentsByOrg,
  selectLlmKeysByOrgAsList,
  (envs, llmKeys) =>
    llmKeys
      .map((llmKey): DeployLlmKeyRow => {
        const env = findEnvById(envs, { id: llmKey.environmentId });
        return {
          ...llmKey,
          envHandle: env.handle,
          totalRequests: llmKey.currentUsage.totalRequests,
          spend: llmKey.currentUsage.spend,
          totalTokens: llmKey.currentUsage.totalTokens,
        };
      })
      .sort((a, b) => b.id.localeCompare(a.id, undefined, { numeric: true })),
);

export const selectLlmKeysByEnvIdForTable = createSelector(
  selectLlmKeysForTable,
  (_: WebState, props: { envId: string }) => props.envId,
  (rows, envId): DeployLlmKeyRow[] =>
    rows.filter((r) => r.environmentId === envId),
);
